import { useCallback, useEffect, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { X, Maximize2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";
import { autoConfidence, isAutoMatched, isAutoResolved } from "@/lib/match-auto";
import { InfoHint } from "@/components/InfoHint";
import { cn } from "@/lib/utils";

type MatchGroupView = components["schemas"]["MatchGroupView"];
type MatchLineView = components["schemas"]["MatchLineView"];
type MatchAllocationView = components["schemas"]["MatchAllocationView"];
type MatchSuggestionView = components["schemas"]["MatchSuggestionView"];
type MatchDnSuggestionView = components["schemas"]["MatchDnSuggestionView"];
// One generic Accept settles a line whatever the variance kind (price / short / over-PO surplus).
type ResolutionAction = components["schemas"]["MatchResolutionAction"];

const STATUS_META: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  Open: { label: "Matching", variant: "secondary" },
  Variance: { label: "Variance - needs review", variant: "destructive" },
  Resolved: { label: "Matched", variant: "default" },
  Synced: { label: "Synced", variant: "outline" },
};

export function BillMatchPanel({
  billId,
  currency,
  editable = true,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  onOpenMatchUi,
}: {
  billId: string;
  currency: string;
  /** The backend allows match changes only while the bill is PendingReview; false renders read-only. */
  editable?: boolean;
  /** Click-through for a suggestion's parent PO (receives the PO id). */
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  /** Click-through for a suggestion's / allocation's parent delivery note (receives the DN id). */
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  onOpenMatchUi?: () => void;
}) {
  const api = useDolfinClient();
  const [view, setView] = useState<MatchGroupView | null>(null);
  // FIRST LOAD ONLY: the spinner collapses the card. Refreshes use `refreshing` so rows and scroll position stay.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acting, setActing] = useState(false);

  const fetchMatch = useCallback(async () => {
    setLoading(true);
    const { data, error } = await api.GET("/v1/bills/{billId}/match-group", {
      params: { path: { billId } },
    });
    // 404 (no match group yet) is expected and not an error to surface.
    setView(error ? null : (data ?? null));
    setLoading(false);
  }, [api, billId]);

  useEffect(() => {
    fetchMatch();
  }, [fetchMatch]);

  // On failure keep the last good view, except on a 404, where the group really is gone.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    const { data, error, response } = await api.GET("/v1/bills/{billId}/match-group", {
      params: { path: { billId } },
    });
    if (response.status === 404) setView(null);
    else if (error) toast.error(apiErrorMessage(error, "Failed to refresh match"));
    else setView(data ?? null);
    setRefreshing(false);
  }, [api, billId]);

  // No method, confidence or quantity: the server derives them, and the client can't see what other bills
  // have already taken from the order.
  async function acceptPoSuggestion(billLineId: string, poLineId: string) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/allocations", {
      params: { path: { id: view!.id } },
      body: { billLineId, poLineId },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to accept suggestion"));
    else toast.success("Purchase-order line matched");
    await refresh();
    setActing(false);
  }

  async function acceptDnSuggestion(billLineId: string, dnLineId: string) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/allocations", {
      params: { path: { id: view!.id } },
      body: { billLineId, dnLineId },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to match delivery"));
    else toast.success("Delivery note matched");
    await refresh();
    setActing(false);
  }

  async function unallocate(allocationId: string) {
    setActing(true);
    const { error } = await api.DELETE("/v1/match-groups/{id}/allocations/{allocationId}", {
      params: { path: { id: view!.id, allocationId } },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to unmatch"));
    else toast.success("Unmatched");
    await refresh();
    setActing(false);
  }

  async function resolve(billLineId: string, action: ResolutionAction) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/resolve", {
      params: { path: { id: view!.id } },
      body: { billLineId, action, notes: null },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to resolve match"));
    else toast.success(RESOLVE_TOAST[action]);
    await refresh();
    setActing(false);
  }

  async function unresolve(billLineId: string) {
    setActing(true);
    const { error } = await api.DELETE("/v1/match-groups/{id}/resolutions/{billLineId}", {
      params: { path: { id: view!.id, billLineId } },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to undo"));
    await refresh();
    setActing(false);
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Checking three-way match...
        </CardContent>
      </Card>
    );
  }

  if (!view) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Three-way match
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No purchase order matched to this bill yet. Raise a PO for the same supplier with matching SKUs and
          the match appears here automatically.
        </CardContent>
      </Card>
    );
  }

  const status = STATUS_META[view.status] ?? { label: view.status, variant: "secondary" as const };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Three-way match
        </CardTitle>
        <div className="flex shrink-0 items-center gap-2">
          {refreshing && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
              <Spinner className="size-3.5" /> Updating
            </span>
          )}
          <Badge variant={status.variant}>{status.label}</Badge>
          {onOpenMatchUi && (
            <Button size="sm" variant="outline" onClick={onOpenMatchUi}>
              <Maximize2 className="size-3.5" />
              Open match workspace
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {view.lines.map((line) => (
          <MatchLineRow
            key={line.billLineId}
            line={line}
            currency={currency}
            acting={acting}
            editable={editable}
            poSuggestions={view.suggestions.filter((s) => s.billLineId === line.billLineId)}
            dnSuggestions={view.dnSuggestions.filter((s) => s.billLineId === line.billLineId)}
            onUnallocate={unallocate}
            onResolve={resolve}
            onUnresolve={unresolve}
            onAcceptPoSuggestion={acceptPoSuggestion}
            onAcceptDnSuggestion={acceptDnSuggestion}
            onPurchaseOrderClick={onPurchaseOrderClick}
            onDeliveryNoteClick={onDeliveryNoteClick}
          />
        ))}

        {!editable &&
          (view.status === "Variance" || view.suggestions.length > 0 || view.dnSuggestions.length > 0) && (
            <p className="border-t pt-3 text-xs text-muted-foreground">
              This match is locked while the bill is in approval. Reopen the bill to change it.
            </p>
          )}
      </CardContent>
    </Card>
  );
}

const RESOLVE_TOAST: Record<ResolutionAction, string> = {
  Accept: "Variance accepted - match resolved",
  AcceptAndUpdateCatalog: "Accepted & catalog flagged - match resolved",
  RequestCreditNote: "Credit note requested",
  Dispute: "Match disputed",
};

function MatchLineRow({
  line,
  currency,
  acting,
  editable,
  poSuggestions,
  dnSuggestions,
  onUnallocate,
  onResolve,
  onUnresolve,
  onAcceptPoSuggestion,
  onAcceptDnSuggestion,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
}: {
  line: MatchLineView;
  currency: string;
  acting: boolean;
  editable: boolean;
  poSuggestions: MatchSuggestionView[];
  dnSuggestions: MatchDnSuggestionView[];
  onUnallocate: (allocationId: string) => void;
  onResolve: (billLineId: string, action: ResolutionAction) => void;
  onUnresolve: (billLineId: string) => void;
  onAcceptPoSuggestion: (billLineId: string, poLineId: string) => void;
  onAcceptDnSuggestion: (billLineId: string, dnLineId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
}) {
  const priceVariance = line.priceVarianceTotal == null ? null : Number(line.priceVarianceTotal);
  const qtyVariance = line.quantityVariance == null ? null : Number(line.quantityVariance);
  const surplus = line.quantityOverOrdered == null ? null : Number(line.quantityOverOrdered);
  const hasVariance = line.hasPriceVariance || line.hasQuantityVariance || line.hasOverBillingVariance;

  return (
    // A line with an open variance takes the destructive tint: it is the one thing here needing a decision.
    <div
      className={cn(
        "rounded-xl border bg-card/60 p-3 text-sm",
        hasVariance && !line.resolution && "border-destructive/30 bg-destructive/5",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{line.description}</span>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          {line.hasPriceVariance && (
            <Badge variant="destructive">
              Price {priceVariance! > 0 ? "+" : ""}
              {formatCurrency(priceVariance!, currency)}
            </Badge>
          )}
          {line.hasQuantityVariance && (
            <Badge variant="destructive">
              {qtyVariance! > 0 ? `Short ${qtyVariance}` : `Over ${Math.abs(qtyVariance!)}`}
            </Badge>
          )}
          {line.hasOverBillingVariance && <Badge variant="destructive">Surplus {surplus}</Badge>}
          {!hasVariance && line.fullyAllocated && line.allocations.length > 0 ? (
            // Say when the engine matched a clean line on its own, rather than a bare "Matched".
            isAutoMatched(line) ? (
              <Badge variant="default" className="gap-1">
                <Sparkles className="size-3" /> Auto-matched
              </Badge>
            ) : (
              <Badge variant="default">Matched</Badge>
            )
          ) : null}
          {line.allocations.length === 0 && <Badge variant="outline">Unmatched</Badge>}
        </div>
      </div>

      <p className="mt-1 text-xs text-muted-foreground tabular-nums">
        Billed {formatCurrency(line.billedNet, currency)} ({Number(line.billedQuantity)} @{" "}
        {formatCurrency(line.billedUnitPrice, currency)})
        {line.allocations.length > 0 && ` · allocated ${formatCurrency(line.allocatedNet, currency)}`}
        {line.receivedQuantity != null && ` · received ${Number(line.receivedQuantity)}`}
      </p>

      {line.allocations.length > 0 && (
        <div className="mt-2 space-y-1">
          {line.allocations.map((a) => (
            <AllocationChip
              key={a.id}
              alloc={a}
              currency={currency}
              acting={acting}
              editable={editable}
              onUnallocate={onUnallocate}
              onPurchaseOrderClick={onPurchaseOrderClick}
              onDeliveryNoteClick={onDeliveryNoteClick}
            />
          ))}
        </div>
      )}

      {editable && (poSuggestions.length > 0 || dnSuggestions.length > 0) && (
        <div className="mt-2 space-y-1">
          {poSuggestions.map((s) => (
            <SuggestionRow
              key={`po:${s.poLineId}`}
              label={s.poNumber ?? "Purchase order"}
              detail={`${Number(s.quantityOrdered)} ordered @ ${formatCurrency(s.orderedUnitPrice, currency)} · ${Math.round(Number(s.confidence) * 100)}%`}
              acting={acting}
              onLabelClick={onPurchaseOrderClick ? () => onPurchaseOrderClick(s.poId) : undefined}
              onMatch={() =>
                onAcceptPoSuggestion(
                  s.billLineId,
                  s.poLineId,
                )
              }
            />
          ))}
          {dnSuggestions.map((s) => (
            <SuggestionRow
              key={`dn:${s.dnLineId}`}
              label={s.dnNumber ?? "Delivery note"}
              detail={`${Number(s.quantityReceived)} received · ${Math.round(Number(s.confidence) * 100)}%`}
              acting={acting}
              onLabelClick={onDeliveryNoteClick ? () => onDeliveryNoteClick(s.dnId) : undefined}
              onMatch={() =>
                onAcceptDnSuggestion(
                  s.billLineId,
                  s.dnLineId,
                )
              }
            />
          ))}
        </div>
      )}

      {hasVariance && line.resolution && (
        <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2 text-xs">
          {/* A variance the ENGINE settled must never read as though a person accepted it — the reviewer needs
              to see what was decided on their behalf (and that Undo takes it back). */}
          {isAutoResolved(line) ? (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Sparkles className="size-3 shrink-0 text-primary" />
              Accepted automatically
              {autoConfidence(line) != null && ` · ${Math.round(autoConfidence(line)! * 100)}% match`}
            </span>
          ) : (
            <span className="text-muted-foreground">
              Resolved: {RESOLUTION_LABEL[line.resolution.action] ?? line.resolution.action}
            </span>
          )}
          {editable && (
            <button
              type="button"
              disabled={acting}
              onClick={() => onUnresolve(line.billLineId)}
              className="font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Undo
            </button>
          )}
        </div>
      )}
      {editable && hasVariance && !line.resolution && (
        <div className="mt-2 flex flex-wrap gap-2 border-t pt-2">
          <Button size="sm" disabled={acting} onClick={() => onResolve(line.billLineId, "Accept")}>
            {line.hasOverBillingVariance ? "Accept surplus" : line.hasQuantityVariance ? "Accept short" : "Accept price"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => onResolve(line.billLineId, "RequestCreditNote")}
          >
            Request credit note
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => onResolve(line.billLineId, "Dispute")}
          >
            Dispute
          </Button>
        </div>
      )}
    </div>
  );
}

const RESOLUTION_LABEL: Record<string, string> = {
  Accept: "variance accepted",
  AcceptAndUpdateCatalog: "accepted & catalog updated",
  RequestCreditNote: "credit note requested",
  Dispute: "disputed",
};

function SuggestionRow({
  label,
  detail,
  acting,
  onLabelClick,
  onMatch,
}: {
  label: string;
  detail: string;
  acting: boolean;
  onLabelClick?: () => void;
  onMatch: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-dashed bg-background px-2 py-1.5 text-xs">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-muted-foreground">
        {onLabelClick ? (
          <button type="button" onClick={onLabelClick} className="font-medium text-primary hover:underline">
            {label}
          </button>
        ) : (
          <span className="font-medium text-foreground">{label}</span>
        )}
        <span>· {detail}</span>
      </div>
      <Button size="sm" variant="outline" className="h-7 shrink-0 px-2.5 text-xs" disabled={acting} onClick={onMatch}>
        Match
      </Button>
    </div>
  );
}

function AllocationChip({
  alloc,
  currency,
  acting,
  editable,
  onUnallocate,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
}: {
  alloc: MatchAllocationView;
  currency: string;
  acting: boolean;
  editable: boolean;
  onUnallocate: (allocationId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border bg-background px-2 py-1 text-xs">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
        {alloc.poLineId && (
          <span className="flex items-center gap-1.5">
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{Number(alloc.matchedQuantity)} ordered</span>
              {alloc.orderedUnitPrice != null && ` @ ${formatCurrency(alloc.orderedUnitPrice, currency)}`}
            </span>
            {alloc.poId && onPurchaseOrderClick && (
              <button
                type="button"
                onClick={() => onPurchaseOrderClick(alloc.poId!)}
                className="text-primary hover:underline"
              >
                view PO
              </button>
            )}
          </span>
        )}
        {alloc.dnLineId && (
          <span className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">
              {alloc.quantityReceived != null ? `${Number(alloc.quantityReceived)} received` : "received"}
            </span>
            {alloc.dnId && onDeliveryNoteClick && (
              <button
                type="button"
                onClick={() => onDeliveryNoteClick(alloc.dnId!)}
                className="text-primary hover:underline"
              >
                view DN
              </button>
            )}
          </span>
        )}
      </div>
      {alloc.autoAccepted && (
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-primary">
          <Sparkles className="size-3" aria-hidden />
          <InfoHint label="this automatic match">
            The matching engine paired these lines on its own
            {alloc.matchConfidence != null
              ? `, with ${Math.round(Number(alloc.matchConfidence) * 100)}% confidence`
              : ""}
            .{editable ? " Remove it with the cross if it is wrong." : ""}
          </InfoHint>
        </span>
      )}
      {editable && (
        <button
          type="button"
          disabled={acting}
          onClick={() => onUnallocate(alloc.id)}
          aria-label="Unmatch"
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-50"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
